import { useEffect, useRef, useState } from 'react'
import { numberWithCommasDecimals } from 'utils/stringFunctions'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'
import useInnovaAxios from './useInnovaAxios'

function useAlerts(byUser, byRole, userName, roleId, alertGroupAlerts) {
  const _isMounted = useRef(false)
  const isLoading = useRef(false)
  const [loading, setLoading] = useState(false)
  const [alertData, setAlertData] = useState(null)
  const [phaseData, setPhaseData] = useState([])
  const [rigData, setRigData] = useState([])

  const getAlertsList = useInnovaAxios({ url: '/alerts/getAlertsList' })

  const updateRoleAlerts = useInnovaAxios({ url: '/alerts/updateRoleAlerts' })

  const updateUserAlerts = useInnovaAxios({ url: '/alerts/updateUserAlerts' })

  const getPhases = useInnovaAxios({ url: '/user/getPhaseCodes' })

  const getRigs = useInnovaAxios({ url: '/user/getRigNames' })

  const updateAlertGroupAlerts = useInnovaAxios({ url: '/alertGroup/updateGroupAlerts' })

  useEffect(() => {
    _isMounted.current = true
    fetchAlertsList()
    return () => {
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPhases = async () => {
    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)
    const response = await getPhases()
    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }

    setPhaseData(response.data || [])

    fetchRigs()
    return success('Phases fetched successfully', response.data)
  }

  const fetchRigs = async () => {
    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)
    const response = await getRigs()
    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }
    setRigData(response.data || [])
    return success('Rigs fetched successfully', response.data)
  }

  const getAlertsKey = (alert) => {
    if (!alert) return crypto.randomUUID()

    const phaseString = Array.isArray(alert.phase) ? alert.phase.sort().join(',') : alert?.phase
    const rigString = Array.isArray(alert.rig) ? alert.rig.sort().join(',') : alert?.rig

    const roundedMin = numberWithCommasDecimals(alert.min, 2)
    const roundedMax = numberWithCommasDecimals(alert.max, 2)

    const roundedMinDepth = numberWithCommasDecimals(alert.minDepth, 2)
    const roundedMaxDepth = numberWithCommasDecimals(alert.maxDepth, 2)

    return `${alert?.name}-${alert.severity}-${phaseString}-${rigString}-${roundedMax}-${roundedMaxDepth}-${roundedMin}-${roundedMinDepth}-${alert.silenceAlert}-${alert.ignoreProjections}`
  }

  const handleSetAlertKeys = (alerts = []) => {
    if (!Array.isArray(alerts)) return []

    const seen = new Map()

    alerts.forEach((alert) => {
      alert.uuid = getAlertsKey(alert)
      if (!seen.has(alert.uuid)) {
        seen.set(alert.uuid, alert)
      }
    })

    return Array.from(seen.values())
  }

  const fetchAlertsList = async () => {
    if (alertGroupAlerts) {
      setAlertData(alertGroupAlerts)
      fetchPhases()
      return
    }
    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)
    const response = await getAlertsList({ roleId: roleId, user: userName, byRole: byRole, byUser: byUser })
    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }

    if (Array.isArray(response.data?.notifications)) {
      response.data.notifications.forEach((notification) => {
        notification.alerts = handleSetAlertKeys(notification.alerts)
      })
    }

    setAlertData(response.data || [])

    fetchPhases()
    return success('Alerts fetched successfully', response.data)
  }

  const updateAlertsByRole = async (alerts) => {
    if (!alerts) return
    if (!Array.isArray(alerts.notifications)) return
    if (alerts.notifications.length === 0) return

    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)

    const response = await updateRoleAlerts({
      alertData: JSON.stringify(alerts.notifications),
      roleId: roleId,
    })

    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }

    fetchAlertsList(false, true)
    return success('Alerts updated successfully')
  }

  const updateAlertsByUser = async (alerts) => {
    if (!alerts) return
    if (!Array.isArray(alerts.notifications)) return
    if (alerts.notifications.length === 0) return

    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)

    const response = await updateUserAlerts({
      alertData: JSON.stringify(alerts.notifications),
      user: userName,
    })

    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }

    fetchAlertsList(true, true)
    return success('Alerts updated successfully')
  }

  const updateAlertsByAlertGroup = async (alerts, uid) => {
    if (!uid) return
    if (!alerts) return
    if (!Array.isArray(alerts.notifications)) return
    if (alerts.notifications.length === 0) return

    if (isLoading.current) return
    isLoading.current = true
    if (_isMounted.current) setLoading(true)

    const response = await updateAlertGroupAlerts({
      alerts: JSON.stringify(alerts),
      uid: uid,
    })

    if (!_isMounted.current) return
    setLoading(false)
    isLoading.current = false

    if (response?.error) {
      return failure(extractErrorMessage(response.error))
    }

    fetchAlertsList(true, true)
    return success('Alerts updated successfully')
  }

  return {
    loading,
    alertData,
    updateAlertsByRole,
    updateAlertsByUser,
    updateAlertsByAlertGroup,
    phaseData,
    rigData,
    getAlertsKey,
  }
}

export default useAlerts
